import {
  TRACKING_526EZ_RUM_SESSION_IDS_FORM,
  TRACKING_526EZ_RUM_SESSION_IDS_STORAGE,
  TRACKING_526EZ_SIDENAV_BACK_BUTTON_CLICKS,
  TRACKING_526EZ_SIDENAV_CONTINUE_BUTTON_CLICKS,
  TRACKING_526EZ_SIDENAV_FEATURE_TOGGLE,
  TRACKING_526EZ_SIDENAV_CLICKS,
  TRACKING_526EZ_0781_FLOW,
} from '../../constants';
import * as datadogRumAddActionModule from './datadogRumAddAction';

// ──────────────────────────────────────────────
// Shared helpers — used by sidenav, BDD SHA, and generic tracking
// ──────────────────────────────────────────────

/**
 * Filters a raw value to a clean array of non-empty string session IDs.
 * Returns an empty array if the input is not an array or contains no valid strings.
 *
 * @param {unknown} value - Raw value to normalize (typically from formData or JSON.parse)
 * @returns {string[]}
 */
const normalizeSessionIds = value => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(id => typeof id === 'string' && id.length > 0);
};

/**
 * Reads the accumulated RUM session count from sessionStorage written by initSessionTracking.
 * Returns { sessionCount } when at least one session has been recorded, or {} when
 * none are available (e.g. DD_RUM not initialized, storage blocked).
 *
 * @returns {{ sessionCount: number } | {}}
 */
export const getSessionTrackingProperties = () => {
  try {
    const raw = sessionStorage.getItem(TRACKING_526EZ_RUM_SESSION_IDS_STORAGE);
    const sessionIds = normalizeSessionIds(raw ? JSON.parse(raw) : []);
    return sessionIds.length > 0 ? { sessionCount: sessionIds.length } : {};
  } catch {
    return {};
  }
};

/**
 * Helper function to track DataDog RUM actions.
 *
 * @param {string} actionName - Name of the action to track
 * @param {object} properties - Properties to attach to the action
 */
export const trackAction = (actionName, properties) => {
  try {
    datadogRumAddActionModule.datadogRumAddAction(actionName, properties);
  } catch (error) {
    // Silent fail - tracking should never break the form
  }
};

/**
 * Increments a click counter in sessionStorage.
 * Returns the new count value, defaulting to 1 if storage is blocked.
 *
 * @param {string} storageKey - The storage key to increment
 * @returns {number} The new click count
 */
export const incrementClickCounter = storageKey => {
  let count = 1;
  try {
    const currentCount = parseInt(
      sessionStorage.getItem(storageKey) || '0',
      10,
    );
    count = currentCount + 1;
    sessionStorage.setItem(storageKey, count.toString());
  } catch (error) {
    // Storage access blocked - continue with default count
  }
  return count;
};

/**
 * Reads all click counters from sessionStorage
 * Returns an object with only counters that have values > 0
 * This allows every tracking action to include comprehensive click count context
 *
 * @returns {object} Object containing backButtonClickCount, continueButtonClickCount, sideNavClickCount (only if > 0)
 */
export const getClickCounts = () => {
  const counts = {};
  try {
    const backButtonCount = parseInt(
      sessionStorage.getItem(TRACKING_526EZ_SIDENAV_BACK_BUTTON_CLICKS) || '0',
      10,
    );
    const continueButtonCount = parseInt(
      sessionStorage.getItem(TRACKING_526EZ_SIDENAV_CONTINUE_BUTTON_CLICKS) ||
        '0',
      10,
    );
    const sideNavCount = parseInt(
      sessionStorage.getItem(TRACKING_526EZ_SIDENAV_CLICKS) || '0',
      10,
    );

    if (backButtonCount > 0) counts.backButtonClickCount = backButtonCount;
    if (continueButtonCount > 0)
      counts.continueButtonClickCount = continueButtonCount;
    if (sideNavCount > 0) counts.sideNavClickCount = sideNavCount;
  } catch (error) {
    // Storage access blocked - return empty object
  }
  return counts;
};

/**
 * Reads common tracking defaults from sessionStorage and the browser.
 * Provides values commonly needed by tracking functions:
 * - sidenav526ezEnabled: written once by Form526EZApp on mount
 * - sourcePath: current window pathname
 *
 * @returns {{ sourcePath: string, sidenav526ezEnabled: boolean|undefined }}
 */
export const getTrackingDefaults = () => {
  let sidenav526ezEnabled;
  let sourcePath = '';

  try {
    sourcePath = window?.location?.pathname || '';
  } catch (error) {
    sourcePath = '';
  }

  try {
    const raw = sessionStorage.getItem(TRACKING_526EZ_SIDENAV_FEATURE_TOGGLE);
    sidenav526ezEnabled = raw !== null ? raw === 'true' : undefined;
  } catch (error) {
    // Storage access blocked (privacy mode, CSP, etc.)
    sidenav526ezEnabled = undefined;
  }

  return {
    sourcePath,
    sidenav526ezEnabled,
  };
};

// ──────────────────────────────────────────────
// Generic form tracking
// ──────────────────────────────────────────────

/**
 * Reads syncModern0781Flow from sessionStorage for RUM defaults (written by Form526EZApp).
 * Only the strings 'true' and 'false' map to booleans; missing, invalid, or any other value is undefined.
 * Omit syncModern0781Flow on payloads when undefined.
 *
 * @returns {{ syncModern0781Flow: boolean|undefined }}
 */
export const get0781TrackingDefaults = () => {
  let syncModern0781Flow;
  try {
    const raw = sessionStorage.getItem(TRACKING_526EZ_0781_FLOW);
    if (raw === 'true') {
      syncModern0781Flow = true;
    } else if (raw === 'false') {
      syncModern0781Flow = false;
    } else {
      syncModern0781Flow = undefined;
    }
  } catch (error) {
    syncModern0781Flow = undefined;
  }
  return { syncModern0781Flow };
};

// ──────────────────────────────────────────────
// Session tracking (all 526EZ users)
// ──────────────────────────────────────────────

/**
 * Appends the current Datadog RUM session ID to the session IDs array in form
 * data and mirrors it to a sessionStorage cache. Called for all 526EZ users
 * when the ITF banner is dismissed.
 *
 * - The cache is read by getSessionTrackingProperties at submit time to include
 *   sessionCount in the claimSubmitted event.
 * - Array length serves as the session count at submission time.
 * - Deduplicates: the same RUM session ID will not be appended twice.
 * - Skips appending a new session ID if DD_RUM is not initialized (localhost, ad-blocked, Cypress);
 *   sessionStorage is still synced from existing formData in that case.
 *
 * @param {object} formData - The current form data from Redux state
 * @param {function} setFormData - Redux dispatch wrapper to update form data
 */
export const initSessionTracking = (formData, setFormData) => {
  const existingIds = normalizeSessionIds(
    formData?.[TRACKING_526EZ_RUM_SESSION_IDS_FORM],
  );
  const rumSessionId = window.DD_RUM?.getInternalContext?.()?.session_id;
  const currentSessionId =
    typeof rumSessionId === 'string' && rumSessionId.length > 0
      ? rumSessionId
      : null;

  const isNewSession =
    currentSessionId !== null && !existingIds.includes(currentSessionId);
  const idsToCache = isNewSession
    ? [...existingIds, currentSessionId]
    : existingIds;

  if (isNewSession && typeof setFormData === 'function') {
    setFormData({
      ...formData,
      [TRACKING_526EZ_RUM_SESSION_IDS_FORM]: idsToCache,
    });
  }

  try {
    if (idsToCache.length > 0) {
      sessionStorage.setItem(
        TRACKING_526EZ_RUM_SESSION_IDS_STORAGE,
        JSON.stringify(idsToCache),
      );
    } else {
      // Clear stale cache when no IDs available
      sessionStorage.removeItem(TRACKING_526EZ_RUM_SESSION_IDS_STORAGE);
    }
  } catch {
    // Storage access blocked - silent fail
  }
};
